<template>
  <base-dialog
    :show="showDialog"
    width="600px"
    data-testid="publishPlayerDialog.dialog"
    title="Publish and Share Statechart Player"
    dialog-id="publishPlayerDialog"
    @complete-dialog="onEnter"
    @cancel-dialog="closeDialog"
  >
    <template #content>
      <div class="d-flex flex-column mb-10">
        <p class="mb-1 text-subtitle-2">Player State</p>
        <p :class="`text-${stateDict[state].stateColor}`">
          {{ stateDict[state].stateDescription }}
        </p>
      </div>
      <div class="d-flex flex-column mb-10" style="gap: 15px">
        <div>
          <p class="mb-1 text-subtitle-2">Player Name</p>
          <v-text-field v-model="playerTitle" variant="outlined" hide-details density="compact" :disabled="inProgress">
          </v-text-field>
        </div>

        <div>
          <p class="mb-1 text-subtitle-2">Access</p>
          <v-select
            v-model="accessOption"
            :disabled="inProgress || true"
            density="compact"
            :items="accessOptions"
            variant="outlined"
            hide-details
            class="mb-2 player-access-select"
            return-object
            ><template #prepend-inner>
              <v-icon size="large" class="align-self-center"> {{ accessOption.icon }}</v-icon>
            </template>
            <template #selection>
              <div class="d-flex flex-column">
                <p class="text-subtitle-2">{{ accessOption.title }}</p>
                <p>{{ accessOption.description }}</p>
              </div>
            </template></v-select
          >
        </div>
        <div>
          <div class="d-flex align-center">
            <p class="text-subtitle-2 mr-2">Initial Control States</p>
            <v-tooltip location="top">
              <template #activator="{ props }">
                <v-btn-icon size="25px" icon="mdi-help-circle-outline" v-bind="props"> </v-btn-icon>
              </template>
              <span
                >WARNING:<br />
                Depending on the initial control state values, the link <br />
                changes. So make sure to set the correct values<br />
                before sharing the link.</span
              >
            </v-tooltip>
          </div>
          <div class="d-flex justify-space-between">
            <v-checkbox
              v-model="autostart"
              label="run on start"
              color="primary"
              hide-details
              density="compact"
              :disabled="inProgress"
            ></v-checkbox>
            <v-checkbox
              v-model="darkTheme"
              label="dark theme"
              color="primary"
              hide-details
              density="compact"
              :disabled="inProgress"
            ></v-checkbox>
            <v-checkbox
              v-model="showEvents"
              label="show events"
              color="primary"
              hide-details
              density="compact"
              :disabled="inProgress"
            ></v-checkbox>
          </div>
        </div>
      </div>
      <div :style="`visibility: ${link ? 'visible' : 'hidden'}`" class="d-flex flex-column mb-3">
        <p class="mb-1 text-subtitle-2">Player Link</p>
        <v-textarea
          class="my-1"
          readonly
          prepend-inner-icon="mdi-link"
          :model-value="link"
          variant="outlined"
          data-testid="publishPlayerDialog.link-input"
          density="compact"
          hide-details
          :disabled="inProgress"
          no-resize
          rows="3"
        >
          <template #append>
            <v-tooltip location="top">
              <template #activator="{ props }">
                <v-btn-icon
                  v-bind="props"
                  data-testid="publishPlayerDialog.copy-link-button"
                  icon="mdi-content-copy"
                  density="compact"
                  @click="copyToClipboard()"
                >
                </v-btn-icon>
              </template>
              <span>Copy Link</span>
            </v-tooltip>
          </template>
        </v-textarea>
      </div>
    </template>
    <template #footer>
      <v-btn-dialog-action
        v-if="stateDict[state].buttonSecondaryAction && stateDict[state].buttonSecondaryText"
        ref="deleteWarningActivator"
        :class="['base-dialog-action-button', { 'text-error': state === 'outdated' || state === 'published' }]"
        :prepend-icon="stateDict[state].buttonSecondaryIcon"
        @click="stateDict[state].buttonSecondaryAction"
        >{{ stateDict[state].buttonSecondaryText }}</v-btn-dialog-action
      >
      <v-btn-dialog-action
        v-if="state === 'outdated'"
        prepend-icon="mdi-share"
        class="base-dialog-action-button"
        @click="openInBrowser"
        >Open in Browser</v-btn-dialog-action
      >
      <loading-button
        ref="CTAExplanationActivator"
        data-testid="publishPlayerDialog.publish-button"
        :delete-mode="state === 'deleting'"
        :action="stateDict[state].buttonCTAAction"
        :loading="inProgress"
        :text="stateDict[state].buttonCTAText"
        :prepend-icon="stateDict[state].buttonCTAIcon"
      >
      </loading-button>
    </template>
  </base-dialog>
  <v-tooltip v-if="state === 'published' || state === 'outdated'" location="top" :activator="deleteWarningActivator">
    <span
      >WARNING:<br />
      If you delete the player, the previously published and<br />
      possibly already shared link becomes invalid.<br />
      Once triggered you <b>cannot stop</b> the process!</span
    >
  </v-tooltip>
  <v-tooltip v-if="state === 'outdated' || state === 'unpublished'" location="top" :activator="CTAExplanationActivator">
    <span v-if="state === 'outdated'">
      WARNING:<br />
      "Update" means, that the statechart of<br />
      the published player will be updated with<br />
      the newest version of your statechart. The<br />
      link stays the same.<br />
      Once triggered you <b>cannot stop</b> the process!
    </span>
    <span v-else>
      "Publish" means, that the current version<br />
      of the statechart will be published to a<br />
      Player on a new link, which you can<br />
      share with people.
    </span>
  </v-tooltip>
</template>

<script setup lang="ts">
import BaseDialog from "../../../shared/BaseDialog.vue";
import LoadingButton from "../../../shared/LoadingButton.vue";
import { PlayerService } from "../../services/player/player-service";
import { AbortRequestEventBus, MessageEventBus, ProgressBarEventBus } from "../../../event-bus";
import { computed, inject, onMounted, ref, watch } from "vue";
import { StatechartsService } from "../../services/statecharts/statecharts-service";
import { PreferenceStore } from "../../services/preference-store";
import { ModelData } from "../../services/statecharts/ModelData";

const playerService: PlayerService = inject<PlayerService>("playerService");

const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");

const compProps = withDefaults(
  defineProps<{
    statechartName?: string;
    modelId: string;
    model?: string;
  }>(),
  { statechartName: "Statechart", model: undefined }
);

const emit = defineEmits(["close"]);

const playerId = ref<string>(undefined);
const playerTitle = ref<string>(compProps.statechartName + "_Player");
const state = ref<StateType>("unpublished");
const lastState = ref<StateType>(undefined);
const showDialog = ref<boolean>(false);

const deleteWarningActivator = ref(null);
const CTAExplanationActivator = ref(null);

const inProgress = computed(() => {
  return state.value === "loading" || state.value === "deleting";
});

type StateType = "unpublished" | "loading" | "published" | "outdated" | "deleting";

interface StateDict {
  unpublished: StateEntry;
  loading: StateEntry;
  published: StateEntry;
  outdated: StateEntry;
  deleting: StateEntry;
}

interface StateEntry {
  stateDescription: string;
  stateColor: string;
  buttonCTAIcon?: string;
  buttonCTAText: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buttonCTAAction: () => any;
  buttonSecondaryIcon?: string;
  buttonSecondaryText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buttonSecondaryAction?: () => any;
}

const stateDict = computed<StateDict>(() => {
  return {
    unpublished: {
      stateDescription: "Player is not published",
      stateColor: "error",
      buttonCTAIcon: "mdi-share-variant",
      buttonCTAText: "Publish",
      buttonCTAAction: () => {
        createPlayer("publish");
      },
      buttonSecondaryText: "Close",
      buttonSecondaryAction: closeDialog,
    },
    loading: {
      stateDescription: lastState.value === "outdated" ? "Updating player..." : "Publishing player...",
      stateColor: "info",
      buttonCTAText: lastState.value === "outdated" ? "Updating..." : "Publishing...",
      buttonCTAAction: () => {},
    },
    published: {
      stateDescription: "Player is published and up to date",
      stateColor: "success",
      buttonCTAIcon: "mdi-share",
      buttonCTAText: "Open in Browser",
      buttonCTAAction: openInBrowser,
      buttonSecondaryIcon: "mdi-delete",
      buttonSecondaryText: "Delete",
      buttonSecondaryAction: deletePlayer,
    },
    outdated: {
      stateDescription: "Player is published but outdated",
      stateColor: "warning",
      buttonCTAIcon: "mdi-update",
      buttonCTAText: "Update",
      buttonCTAAction: () => {
        createPlayer("update");
      },
      buttonSecondaryIcon: "mdi-delete",
      buttonSecondaryText: "Delete",
      buttonSecondaryAction: deletePlayer,
    },
    deleting: {
      stateDescription: "Deleting player...",
      stateColor: "error",
      buttonCTAText: "Deleting...",
      buttonCTAAction: () => {},
    },
  };
});
const accessOptionId = ref<"public" | "private">("public");
const autostart = ref<boolean>(true);
const showEvents = ref<boolean>(true);

const preferenceStore = PreferenceStore.INSTANCE;

const darkTheme = ref<boolean>(preferenceStore.isDark);

const baseUrl = ref<string>("");

const link = computed<string>(() => {
  let finalLink = baseUrl.value;
  if (finalLink) {
    finalLink = baseUrl.value;
    if (darkTheme.value) {
      finalLink += "&theme=dark";
    }
    if (!showEvents.value) {
      finalLink += "&show-members=false";
    }
    if (!autostart.value) {
      finalLink += "&start=false";
    }
  }
  return finalLink;
});

onMounted(async () => {
  ProgressBarEventBus.emit("startTask");
  await playerService
    .getModelMetadata(compProps.modelId)
    .then((modelInfo) => {
      initCurrentState(modelInfo);
      showDialog.value = true;
    })
    .catch(() => {
      emit("close");
    });
  // TODO playertitle at all needed? #862
  playerTitle.value = compProps.statechartName + "_Player";
  ProgressBarEventBus.emit("finishedTask");
});

watch([playerTitle, accessOptionId], () => {
  // TODO how to handle? for later? as title not needed? and access fixed?
  if (state.value === "published") state.value = "outdated";
});

watch([autostart, showEvents, darkTheme], () => {
  if (state.value === "published" || state.value === "outdated") {
    MessageEventBus.emit("success", {
      id: "publish-player-dialog",
      text: "Link got updated with new player settings.",
    });
  }
});

interface AccessOption {
  id: "public" | "private";
  title: string;
  description: string;
  icon: string;
}

const accessOptions: AccessOption[] = [
  {
    id: "public",
    title: "Public access",
    description: "Anyone with the link can interact with the player",
    icon: "mdi-earth",
  },
  {
    id: "private",
    title: "Private access",
    description: "-",
    icon: "mdi-lock",
  },
];

const accessOption = computed({
  get() {
    return accessOptions.find((el) => el.id === accessOptionId.value);
  },
  set(value: AccessOption) {
    accessOptionId.value = value.id;
  },
});

const onEnter = () => {
  const CTAAction = stateDict[state.value].buttonCTAAction;
  if (CTAAction) CTAAction();
};

function closeDialog() {
  if (inProgress.value) {
    AbortRequestEventBus.emit("abort");
    setToLastState();
  }
  emit("close");
}

function initCurrentState(modelInfo: ModelData) {
  if (modelInfo.players?.length > 0) {
    playerId.value = modelInfo.players[0].playerId;
    baseUrl.value = buildBaseUrl(playerId.value);
    if (modelInfo.modifiedAt > modelInfo.players[0].creationDate) {
      state.value = "outdated";
    } else {
      state.value = "published";
    }
  } else {
    state.value = "unpublished";
  }
}

function setToLastState() {
  state.value = lastState.value;
  lastState.value = undefined;
}

function setStateToLoading() {
  lastState.value = state.value;
  state.value = "loading";
}

async function createPlayer(mode: "update" | "publish") {
  setStateToLoading();

  const model = compProps.model ?? (await cloudStatechartService.getModel(compProps.modelId));

  if (model) {
    await playerService
      .publishOrUpdate(compProps.modelId, model, mode, playerId.value)
      .then(async (id: string) => {
        playerId.value = id;
        baseUrl.value = buildBaseUrl(id);
        state.value = "published";
        let successMsg: string;
        if (mode === "publish") {
          successMsg = "Player link has been successfully created.";
        } else {
          successMsg = "Player has been successfully updated.";
        }
        MessageEventBus.emit("success", {
          id: "publish-player-dialog",
          text: successMsg,
        });
      })
      .catch(() => {
        setToLastState();
      });
  } else {
    setToLastState();
  }
}

function buildBaseUrl(playerId: string) {
  return "https://" + import.meta.env.VUE_APP_PLAYER_URL + "?model=" + playerId;
}

async function copyToClipboard(successMsg = "Link copied to clipboard.") {
  await navigator.clipboard
    .writeText(link.value)
    .then(() => {
      if (successMsg) {
        MessageEventBus.emit("success", {
          id: "publish-player-dialog",
          text: successMsg,
        });
      }
    })
    .catch(() => {
      MessageEventBus.emit("error", {
        id: "publish-player-dialog",
        text: "Link couldn't be copied to Clipboard.",
      });
    });
}

function openInBrowser() {
  window.open(link.value, "_blank");
}

async function deletePlayer() {
  lastState.value = state.value;
  state.value = "deleting";

  await playerService
    .delete(compProps.modelId, playerId.value)
    .then(() => {
      state.value = "unpublished";
      baseUrl.value = "";
      playerId.value = undefined;
    })
    .catch(() => {
      setToLastState();
    });
}
</script>

<style scoped>
.v-alert--variant-outlined {
  border-color: rgb(var(--v-theme-warning));
}
</style>
<style>
.player-access-select .v-field__prepend-inner {
  padding-top: 0;
  padding-right: 4px;
}
</style>
